(() => {
  const STAR_COLOR = '#B42E34';
  const PRESSED_GREEN = '#45A65A';
  const PAINTED_FLAG = 'yotpoStarsPainted';
  const UNCLIPPED_FLAG = 'yotpoUnclipped';
  const YOTPO_ROOT_SELECTOR = '.yotpo-widget-instance, .yotpo-reviews-widget, [class*="yotpo-reviews"]';

  let enhanceQueued = false;
  let observerPaused = false;
  let retryTimer = null;

  const getYotpoReviewLabels = () => ({
    searchReviews: window.theme?.strings?.yotpoSearchReviews || 'Search reviews',
    rating: window.theme?.strings?.yotpoRating || 'Rating',
    filterRatings: window.theme?.strings?.yotpoFilterRatings || 'Ratings'
  });

  const normalizeLabel = (value) => value?.trim().toLowerCase();

  const resolveYotpoRoots = (root = document) => {
    if (root === document) {
      const widgets = document.querySelectorAll(YOTPO_ROOT_SELECTOR);
      return widgets.length ? [...widgets] : [];
    }

    if (root.matches?.('[class*="yotpo"]')) return [root];

    const nested = root.querySelectorAll(YOTPO_ROOT_SELECTOR);
    if (nested.length) return [...nested];

    return hasYotpoContent(root) ? [root] : [];
  };

  const runWithoutObserver = (fn) => {
    observerPaused = true;
    try {
      fn();
    } finally {
      observerPaused = false;
    }
  };

  const scheduleEnhance = (root = document) => {
    if (enhanceQueued) return;
    enhanceQueued = true;

    requestAnimationFrame(() => {
      enhanceQueued = false;
      enhanceYotpo(root);
    });
  };

  const unclipYotpoFieldAncestors = (control) => {
    if (control.dataset[UNCLIPPED_FLAG]) return;

    const yotpoRoot = control.closest('[class*="yotpo"]');
    if (!yotpoRoot) return;

    let node = control.parentElement;

    while (node && node !== yotpoRoot) {
      const { overflow, overflowX, overflowY } = window.getComputedStyle(node);
      if ([overflow, overflowX, overflowY].some((value) => value === 'hidden' || value === 'clip')) {
        node.classList.add('yotpo-reviews-field-unclipped');
      }

      node = node.parentElement;
    }

    const rootOverflow = window.getComputedStyle(yotpoRoot);
    if ([rootOverflow.overflow, rootOverflow.overflowX, rootOverflow.overflowY].some((value) => value === 'hidden' || value === 'clip')) {
      yotpoRoot.classList.add('yotpo-reviews-field-unclipped');
    }

    control.dataset[UNCLIPPED_FLAG] = 'true';
  };

  const wrapYotpoFieldControl = (control) => {
    const existingWrapper = control.closest('.yotpo-reviews-field');
    if (existingWrapper) return existingWrapper;

    const wrapper = document.createElement('div');
    wrapper.className = 'yotpo-reviews-field';
    control.parentNode.insertBefore(wrapper, control);
    wrapper.appendChild(control);
    return wrapper;
  };

  const finishYotpoFieldLabel = (control) => {
    control.removeAttribute('placeholder');
    control.dataset.yotpoA11yLabel = 'true';
  };

  const ensureSearchFieldStructure = (control, label) => {
    const searchFilter = control.closest('.yotpo-search-filter, .yotpo-free-search-filter-container');
    if (!searchFilter) return null;

    searchFilter.classList.remove('yotpo-reviews-field', 'yotpo-reviews-field--search');

    const controlRow = searchFilter.querySelector(':scope > .yotpo-reviews-field-control-row');
    if (controlRow) {
      while (controlRow.firstChild) {
        searchFilter.insertBefore(controlRow.firstChild, controlRow);
      }
      controlRow.remove();
    }

    let field = searchFilter.parentElement?.classList.contains('yotpo-reviews-field--search')
      ? searchFilter.parentElement
      : null;

    if (!field) {
      field = document.createElement('div');
      field.className = 'yotpo-reviews-field yotpo-reviews-field--search';
      searchFilter.parentNode.insertBefore(field, searchFilter);
      field.appendChild(searchFilter);
    }

    if (label && label.parentNode !== field) {
      field.insertBefore(label, searchFilter);
    }

    unclipYotpoFieldAncestors(field);
    return field;
  };

  const ensureRatingFieldStructure = (control, label) => {
    const scoreFilter = control.closest('.yotpo-score-filter-container');
    if (!scoreFilter) return null;

    scoreFilter.classList.remove('yotpo-reviews-field', 'yotpo-reviews-field--rating');

    let field = scoreFilter.parentElement?.classList.contains('yotpo-reviews-field--rating')
      ? scoreFilter.parentElement
      : null;

    if (!field) {
      field = document.createElement('div');
      field.className = 'yotpo-reviews-field yotpo-reviews-field--rating';
      scoreFilter.parentNode.insertBefore(field, scoreFilter);
      field.appendChild(scoreFilter);
    }

    if (label && label.parentNode !== field) {
      field.insertBefore(label, scoreFilter);
    }

    unclipYotpoFieldAncestors(field);
    return field;
  };

  const cleanupOrphanedYotpoFields = (root = document) => {
    root.querySelectorAll('.yotpo-reviews-field--search, .yotpo-reviews-field--rating').forEach((field) => {
      const hasSearchFilter = field.querySelector('.yotpo-search-filter, .yotpo-free-search-filter-container');
      const hasScoreFilter = field.querySelector('.yotpo-score-filter-container');

      if (hasSearchFilter || hasScoreFilter) return;

      field.querySelectorAll('.yotpo-reviews-field-label').forEach((orphanLabel) => orphanLabel.remove());
      field.remove();
    });
  };

  const addYotpoFieldLabel = (control, labelText) => {
    if (!control) return;

    const searchFilter = control.closest('.yotpo-search-filter, .yotpo-free-search-filter-container');
    const scoreFilter = control.closest('.yotpo-score-filter-container');

    if (control.dataset.yotpoA11yLabel) {
      const yotpoRoot = control.closest('[class*="yotpo"]');
      let labelToUse = control.id
        ? yotpoRoot?.querySelector(`label.yotpo-reviews-field-label[for="${control.id}"]`)
        : null;

      if (!labelToUse) {
        labelToUse = searchFilter?.closest('.yotpo-reviews-field--search')?.querySelector(':scope > .yotpo-reviews-field-label')
          || scoreFilter?.closest('.yotpo-reviews-field--rating')?.querySelector(':scope > .yotpo-reviews-field-label');
      }

      if (!labelToUse) {
        labelToUse = document.createElement('label');
        labelToUse.className = 'label yotpo-reviews-field-label';
        labelToUse.htmlFor = control.id;
        labelToUse.textContent = labelText;
      }

      if (searchFilter) {
        ensureSearchFieldStructure(control, labelToUse);
      } else if (scoreFilter) {
        ensureRatingFieldStructure(control, labelToUse);
      }
      return;
    }

    const fieldId = control.id || `yotpo-review-field-${labelText.toLowerCase().replace(/\s+/g, '-')}`;
    if (!control.id) control.id = fieldId;

    const yotpoRoot = control.closest('[class*="yotpo"]');
    const existingLabel = yotpoRoot?.querySelector(`label.yotpo-reviews-field-label[for="${fieldId}"]`);
    if (existingLabel) {
      if (searchFilter) {
        ensureSearchFieldStructure(control, existingLabel);
      } else if (scoreFilter) {
        ensureRatingFieldStructure(control, existingLabel);
      }
      finishYotpoFieldLabel(control);
      return;
    }

    const label = document.createElement('label');
    label.className = 'label yotpo-reviews-field-label';
    label.htmlFor = fieldId;
    label.textContent = labelText;

    if (searchFilter) {
      ensureSearchFieldStructure(control, label);
      finishYotpoFieldLabel(control);
      return;
    }

    if (scoreFilter) {
      ensureRatingFieldStructure(control, label);
      finishYotpoFieldLabel(control);
      return;
    }

    const wrapper = wrapYotpoFieldControl(control);
    wrapper.insertBefore(label, control);
    unclipYotpoFieldAncestors(wrapper);
    finishYotpoFieldLabel(control);
  };

  const enhanceYotpoReviewFilters = (root) => {
    const labels = getYotpoReviewLabels();
    const searchReviewsLabel = normalizeLabel(labels.searchReviews);
    const ratingLabel = normalizeLabel(labels.rating);

    resolveYotpoRoots(root).forEach((yotpoRoot) => {
      yotpoRoot.querySelectorAll('input[placeholder]').forEach((input) => {
        const placeholder = normalizeLabel(input.placeholder);

        if (placeholder === searchReviewsLabel) {
          addYotpoFieldLabel(input, labels.searchReviews);
        } else if (placeholder === ratingLabel) {
          addYotpoFieldLabel(input, labels.filterRatings);
        }
      });

      yotpoRoot.querySelectorAll('input.yotpo-search-input[data-yotpo-a11y-label]').forEach((input) => {
        addYotpoFieldLabel(input, labels.searchReviews);
      });

      yotpoRoot.querySelectorAll('.yotpo-score-filter-container [role="combobox"], .yotpo-score-filter-container select').forEach((control) => {
        addYotpoFieldLabel(control, labels.filterRatings);
      });

      yotpoRoot.querySelectorAll('[role="combobox"], select').forEach((control) => {
        if (control.dataset.yotpoA11yLabel) return;
        if (control.closest('.yotpo-score-filter-container')) return;

        const controlLabel = normalizeLabel(
          control.getAttribute('aria-label')
          || control.getAttribute('placeholder')
          || control.textContent
        );

        if (controlLabel === ratingLabel || controlLabel.startsWith(`${ratingLabel} `)) {
          addYotpoFieldLabel(control, labels.filterRatings);
        }
      });
    });
  };

  const paintYotpoStars = (root) => {
    resolveYotpoRoots(root).forEach((yotpoRoot) => {
      yotpoRoot.querySelectorAll('svg.yotpo-sr-star-full path, svg.yotpo-sr-star-half path').forEach((path) => {
        if (path.dataset[PAINTED_FLAG]) return;
        path.style.setProperty('fill', STAR_COLOR, 'important');
        path.style.setProperty('stroke', STAR_COLOR, 'important');
        path.dataset[PAINTED_FLAG] = 'true';
      });

      yotpoRoot.querySelectorAll('svg.yotpo-sr-star-empty path').forEach((path) => {
        if (path.dataset[PAINTED_FLAG]) return;
        path.style.setProperty('fill', 'none', 'important');
        path.style.setProperty('stroke', STAR_COLOR, 'important');
        path.style.setProperty('stroke-width', '1.5px', 'important');
        path.dataset[PAINTED_FLAG] = 'true';
      });

      yotpoRoot.querySelectorAll('svg.yotpo-sr-star-full stop, svg.yotpo-sr-star-empty stop').forEach((stop) => {
        if (stop.dataset[PAINTED_FLAG]) return;
        stop.style.setProperty('stop-color', STAR_COLOR, 'important');
        stop.dataset[PAINTED_FLAG] = 'true';
      });

      yotpoRoot.querySelectorAll('svg.yotpo-sr-star-half').forEach((svg) => {
        if (svg.dataset[PAINTED_FLAG]) return;
        const stops = svg.querySelectorAll('stop');
        if (stops[0]) stops[0].style.setProperty('stop-color', STAR_COLOR, 'important');
        if (stops[1]) stops[1].style.setProperty('stop-color', '#FFFFFF', 'important');
        svg.dataset[PAINTED_FLAG] = 'true';
      });

      yotpoRoot.querySelectorAll('.yotpo-icon-star, .yotpo-icon-half-star, .yotpo-icon-empty-star, .yotpo-review-stars .yotpo-icon').forEach((icon) => {
        if (icon.dataset[PAINTED_FLAG]) return;
        icon.style.setProperty('color', STAR_COLOR, 'important');
        icon.dataset[PAINTED_FLAG] = 'true';
      });
    });
  };

  const paintYotpoRadioIcons = (root) => {
    resolveYotpoRoots(root).forEach((yotpoRoot) => {
      yotpoRoot.querySelectorAll('svg.yotpo-radio-icon path').forEach((path) => {
        path.style.setProperty('fill', PRESSED_GREEN, 'important');
      });
    });
  };

  const filtersAreEnhanced = () => {
    const widgets = document.querySelectorAll(YOTPO_ROOT_SELECTOR);
    if (!widgets.length) return false;

    return [...widgets].every((widget) => {
      const filtersPanel = widget.querySelector('.yotpo-filters-top-panel');
      if (!filtersPanel) return true;

      const needsSearch = Boolean(filtersPanel.querySelector('.yotpo-search-input, .yotpo-search-filter'));
      const needsRating = Boolean(filtersPanel.querySelector('.yotpo-score-filter-container'));
      const hasSearch = Boolean(filtersPanel.querySelector('.yotpo-search-input[data-yotpo-a11y-label]'));
      const hasRating = Boolean(filtersPanel.querySelector('.yotpo-score-filter-container [data-yotpo-a11y-label]'));

      if (needsSearch && !hasSearch) return false;
      if (needsRating && !hasRating) return false;
      return true;
    });
  };

  const hasYotpoContent = (node) => {
    if (node.nodeType !== 1) return false;
    return node.matches?.('[class*="yotpo"]') || Boolean(node.querySelector?.('[class*="yotpo"]'));
  };

  const findYotpoRootFromNode = (node) => {
    if (node.nodeType !== 1) return null;
    if (node.matches?.(YOTPO_ROOT_SELECTOR)) return node;
    return node.closest?.(YOTPO_ROOT_SELECTOR) || node.querySelector?.(YOTPO_ROOT_SELECTOR);
  };

  const enhanceYotpo = (root = document) => {
    runWithoutObserver(() => {
      const roots = resolveYotpoRoots(root);
      if (!roots.length) return;

      roots.forEach((yotpoRoot) => {
        cleanupOrphanedYotpoFields(yotpoRoot);
        paintYotpoStars(yotpoRoot);
        enhanceYotpoReviewFilters(yotpoRoot);
      });
    });
  };

  const startRetryLoop = () => {
    let retries = 0;

    retryTimer = window.setInterval(() => {
      if (filtersAreEnhanced()) {
        window.clearInterval(retryTimer);
        retryTimer = null;
        return;
      }

      enhanceYotpo();
      retries += 1;

      if (retries >= 20) {
        window.clearInterval(retryTimer);
        retryTimer = null;
      }
    }, 500);
  };

  const init = () => {
    enhanceYotpo();
    startRetryLoop();

    const observer = new MutationObserver((mutations) => {
      if (observerPaused) return;

      const rootsToEnhance = new Set();
      let needsCleanup = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const attr = mutation.attributeName;
          if (attr !== 'aria-pressed' && attr !== 'aria-checked') return;

          const yotpoRoot = mutation.target.closest?.('[class*="yotpo"]');
          if (yotpoRoot) rootsToEnhance.add(yotpoRoot);
          return;
        }

        if (mutation.removedNodes.length) {
          mutation.removedNodes.forEach((node) => {
            if (hasYotpoContent(node)) needsCleanup = true;
          });
        }

        mutation.addedNodes.forEach((node) => {
          const yotpoRoot = findYotpoRootFromNode(node);
          if (yotpoRoot) rootsToEnhance.add(yotpoRoot);
        });
      });

      if (needsCleanup) {
        scheduleEnhance(document);
        return;
      }

      if (!rootsToEnhance.size) return;

      rootsToEnhance.forEach((yotpoRoot) => scheduleEnhance(yotpoRoot));

      // Radio icon colors can change with filter state.
      runWithoutObserver(() => {
        rootsToEnhance.forEach((yotpoRoot) => paintYotpoRadioIcons(yotpoRoot));
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-pressed', 'aria-checked']
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
